<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import inboxesAPI from 'dashboard/api/inboxes';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inboxId: {
    type: [String, Number],
    required: true,
  },
});

const router = useRouter();
const route = useRoute();

const VERTICAL_OPTIONS = [
  { value: 'FINANCE', label: 'Finanças e bancos' },
  { value: 'AUTO', label: 'Automotivo' },
  { value: 'BEAUTY', label: 'Beleza' },
  { value: 'APPAREL', label: 'Roupas' },
  { value: 'EDU', label: 'Educação' },
  { value: 'ENTERTAIN', label: 'Entretenimento' },
  { value: 'EVENT_PLAN', label: 'Eventos' },
  { value: 'GROCERY', label: 'Mercado' },
  { value: 'GOVT', label: 'Governo' },
  { value: 'HOTEL', label: 'Hotelaria' },
  { value: 'HEALTH', label: 'Saúde' },
  { value: 'NONPROFIT', label: 'Sem fins lucrativos' },
  { value: 'PROF_SERVICES', label: 'Serviços profissionais' },
  { value: 'RETAIL', label: 'Varejo' },
  { value: 'TRAVEL', label: 'Viagens' },
  { value: 'RESTAURANT', label: 'Restaurante' },
  { value: 'OTHER', label: 'Outros' },
  { value: 'UNDEFINED', label: 'Não definida' },
  { value: 'NOT_A_BIZ', label: 'Não é empresa' },
];

const getters = useStoreGetters();
const isLoading = ref(false);
const isSaving = ref(false);
const isEditing = ref(false);
const showTemplateManager = ref(false);
const isLoadingTemplates = ref(false);
const isSavingTemplate = ref(false);
const profile = ref(null);
const templates = ref([]);
const selectedTemplate = ref(null);
const imageFailed = ref(false);
const loadError = ref('');
const templateError = ref('');
const templateMode = ref('create');
const form = ref({
  about: '',
  description: '',
  vertical: '',
  address: '',
  email: '',
  websites: '',
  profile_picture_url: '',
});
const templateFilters = ref({
  query: '',
  status: '',
  category: '',
});
const emptyTemplateForm = () => ({
  name: '',
  category: 'UTILITY',
  language: 'pt_BR',
  parameter_format: 'POSITIONAL',
  body: '',
  footer_text: '',
  componentsText: JSON.stringify(
    [{ type: 'BODY', text: 'Olá! Como podemos ajudar?' }],
    null,
    2
  ),
});
const templateForm = ref(emptyTemplateForm());

const TEMPLATE_STATUS_OPTIONS = ['APPROVED', 'PENDING', 'REJECTED', 'PAUSED'];
const TEMPLATE_CATEGORY_OPTIONS = ['UTILITY', 'MARKETING', 'AUTHENTICATION'];

const normalizeTemplateName = name =>
  String(name || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');

const inbox = computed(() =>
  getters['inboxes/getInboxes'].value.find(
    item => String(item.id) === String(props.inboxId)
  )
);

const phone = computed(() => profile.value?.phone || {});
const businessProfile = computed(() => profile.value?.business_profile || {});
const profileImage = computed(
  () =>
    !imageFailed.value &&
    (businessProfile.value.profile_picture_url ||
      phone.value.profile_picture_url ||
      inbox.value?.avatar_url)
);
const displayName = computed(
  () => phone.value.verified_name || inbox.value?.business_name || inbox.value?.name
);
const displayPhone = computed(
  () =>
    phone.value.display_phone_number ||
    profile.value?.inbox?.phone_number ||
    inbox.value?.phone_number
);
const websites = computed(() =>
  Array.isArray(businessProfile.value.websites)
    ? businessProfile.value.websites
    : []
);
const initials = computed(() =>
  (displayName.value || 'WA')
    .split(' ')
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase()
);

const aboutText = computed(
  () => businessProfile.value.about || businessProfile.value.description
);

const profileRows = computed(() =>
  [
    {
      icon: 'i-lucide-message-square-text',
      title: 'Status',
      label: aboutText.value,
    },
    {
      icon: 'i-lucide-file-text',
      title: 'Descrição',
      label: businessProfile.value.description,
    },
    {
      icon: 'i-lucide-briefcase-business',
      title: 'Categoria',
      label: formatVertical(businessProfile.value.vertical),
    },
    {
      icon: 'i-lucide-map-pin',
      title: 'Endereço',
      label: businessProfile.value.address,
    },
    {
      icon: 'i-lucide-mail',
      title: 'E-mail',
      label: businessProfile.value.email,
      href: businessProfile.value.email
        ? `mailto:${businessProfile.value.email}`
        : undefined,
    },
    ...websites.value.map(website => ({
      icon: 'i-lucide-globe',
      title: 'Site',
      label: website,
      href: normalizeWebsite(website),
    })),
  ].filter(row => row.label)
);

const statusItems = computed(() =>
  [
    ['Status do nome', formatStatus(phone.value.name_status)],
    ['Qualidade', formatStatus(phone.value.quality_rating)],
    ['Verificação', formatStatus(phone.value.code_verification_status)],
    ['Modo da conta', formatStatus(phone.value.account_mode)],
    ['Plataforma', formatStatus(phone.value.platform_type)],
    ['Limite de mensagens', formatStatus(phone.value.messaging_limit_tier)],
  ].filter(([, value]) => value)
);

const visibleTemplates = computed(() => {
  const query = templateFilters.value.query.trim().toLowerCase();
  return templates.value.filter(template => {
    const matchesQuery =
      !query ||
      template.name?.toLowerCase().includes(query) ||
      template.components?.some(component =>
        component.text?.toLowerCase().includes(query)
      );
    const matchesStatus =
      !templateFilters.value.status ||
      template.status === templateFilters.value.status;
    const matchesCategory =
      !templateFilters.value.category ||
      template.category === templateFilters.value.category;
    return matchesQuery && matchesStatus && matchesCategory;
  });
});

const templateSummary = template => {
  const body = template?.components?.find(component => component.type === 'BODY');
  return body?.text || 'Template sem corpo de mensagem';
};

const statusLabel = status => {
  const labels = {
    APPROVED: 'Aprovado',
    PENDING: 'Em análise',
    REJECTED: 'Reprovado',
    PAUSED: 'Pausado',
    DISABLED: 'Desativado',
  };
  return labels[status] || status || 'Sem status';
};

const categoryLabel = category => {
  const labels = {
    UTILITY: 'Utilidade',
    MARKETING: 'Marketing',
    AUTHENTICATION: 'Autenticação',
  };
  return labels[category] || category || 'Sem categoria';
};

const parseTemplateComponents = () => {
  try {
    const components = JSON.parse(templateForm.value.componentsText || '[]');
    if (!Array.isArray(components)) throw new Error('Components must be an array');
    templateError.value = '';
    return components;
  } catch {
    templateError.value = 'JSON de componentes inválido';
    return null;
  }
};

const buildTemplatePayload = () => {
  const components = parseTemplateComponents();
  if (!components) return null;

  return {
    name: normalizeTemplateName(templateForm.value.name),
    category: templateForm.value.category,
    language: templateForm.value.language.trim(),
    parameter_format: templateForm.value.parameter_format,
    footer_text: templateForm.value.footer_text.trim(),
    components,
  };
};

const syncForm = () => {
  const data = businessProfile.value;
  form.value = {
    about: data.about || '',
    description: data.description || '',
    vertical: data.vertical || '',
    address: data.address || '',
    email: data.email || '',
    websites: websites.value.join('\n'),
    profile_picture_url: data.profile_picture_url || '',
  };
};

const fetchProfile = async () => {
  isLoading.value = true;
  imageFailed.value = false;
  loadError.value = '';
  try {
    const { data } = await inboxesAPI.getWhatsappProfile(props.inboxId);
    profile.value = data;
    syncForm();
  } catch (error) {
    loadError.value =
      error?.response?.data?.message ||
      'Não foi possível carregar o perfil do WhatsApp';
  } finally {
    isLoading.value = false;
  }
};

const fetchTemplates = async () => {
  isLoadingTemplates.value = true;
  templateError.value = '';
  try {
    const { data } = await inboxesAPI.getWhatsappTemplates(props.inboxId, {
      limit: 200,
    });
    templates.value = data.data || [];
    if (!selectedTemplate.value && templates.value.length) {
      selectTemplate(templates.value[0]);
    }
  } catch (error) {
    templateError.value =
      error?.response?.data?.message ||
      error?.response?.data?.error?.message ||
      'Não foi possível carregar os templates do WhatsApp';
  } finally {
    isLoadingTemplates.value = false;
  }
};

const updateProfile = async () => {
  isSaving.value = true;
  loadError.value = '';

  const payload = {
    about: form.value.about,
    description: form.value.description,
    vertical: form.value.vertical,
    address: form.value.address,
    email: form.value.email,
    profile_picture_url: form.value.profile_picture_url,
    websites: form.value.websites
      .split(/[\n,]/)
      .map(item => item.trim())
      .filter(Boolean),
  };

  try {
    const { data } = await inboxesAPI.updateWhatsappProfile(
      props.inboxId,
      payload
    );
    profile.value = data;
    imageFailed.value = false;
    isEditing.value = false;
    syncForm();
    useAlert('Perfil do WhatsApp atualizado');
  } catch (error) {
    loadError.value =
      error?.response?.data?.message ||
      'Não foi possível salvar o perfil do WhatsApp';
  } finally {
    isSaving.value = false;
  }
};

const cancelEdit = () => {
  syncForm();
  isEditing.value = false;
  loadError.value = '';
};

const openTemplateManager = async () => {
  router.push({
    name: 'whatsapp_templates',
    params: { accountId: route.params.accountId, inbox_id: props.inboxId },
  });
};

const newTemplate = () => {
  templateMode.value = 'create';
  selectedTemplate.value = null;
  templateForm.value = emptyTemplateForm();
  templateError.value = '';
};

const selectTemplate = template => {
  selectedTemplate.value = template;
  templateMode.value = 'edit';
  const body = template.components?.find(component => component.type === 'BODY');
  const footer = template.components?.find(component => component.type === 'FOOTER');
  templateForm.value = {
    name: template.name || '',
    category: template.category || 'UTILITY',
    language: template.language || 'pt_BR',
    parameter_format: template.parameter_format || 'POSITIONAL',
    body: body?.text || '',
    footer_text: footer?.text || '',
    componentsText: JSON.stringify(template.components || [], null, 2),
  };
  templateError.value = '';
};

const saveTemplate = async () => {
  const payload = buildTemplatePayload();
  if (!payload) return;
  if (templateMode.value === 'create' && !payload.name) {
    templateError.value = 'Informe o nome do template';
    return;
  }

  isSavingTemplate.value = true;
  templateError.value = '';
  try {
    if (templateMode.value === 'edit' && selectedTemplate.value?.id) {
      await inboxesAPI.updateWhatsappTemplate(
        props.inboxId,
        selectedTemplate.value.id,
        payload
      );
      useAlert('Template enviado para atualização na Meta');
    } else {
      await inboxesAPI.createWhatsappTemplate(props.inboxId, payload);
      useAlert('Template criado e enviado para análise da Meta');
    }
    await fetchTemplates();
  } catch (error) {
    templateError.value =
      error?.response?.data?.error?.message ||
      error?.response?.data?.message ||
      'Não foi possível salvar o template na Meta';
  } finally {
    isSavingTemplate.value = false;
  }
};

const deleteTemplate = async template => {
  if (!template?.name) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Excluir o template "${template.name}" na Meta?`)) return;

  isSavingTemplate.value = true;
  templateError.value = '';
  try {
    await inboxesAPI.deleteWhatsappTemplate(props.inboxId, template.name);
    useAlert('Template excluído na Meta');
    newTemplate();
    await fetchTemplates();
  } catch (error) {
    templateError.value =
      error?.response?.data?.error?.message ||
      error?.response?.data?.message ||
      'Não foi possível excluir o template na Meta';
  } finally {
    isSavingTemplate.value = false;
  }
};

const shareProfile = async () => {
  const link = profile.value?.wa_link;
  if (!link) return;

  try {
    await navigator.clipboard.writeText(link);
    useAlert('Link do WhatsApp copiado');
  } catch {
    window.open(link, '_blank', 'noopener,noreferrer');
  }
};

const normalizeWebsite = website => {
  if (!website) return undefined;
  return website.startsWith('http') ? website : `https://${website}`;
};

const formatVertical = vertical => {
  if (!vertical) return '';
  const option = VERTICAL_OPTIONS.find(item => item.value === vertical);
  if (option) return option.label;
  if (vertical === 'PROFESSIONAL_SERVICES') return 'Serviços profissionais';
  if (vertical === 'REAL_ESTATE') return 'Imóveis';
  return vertical.replace(/_/g, ' ').toLowerCase();
};

const formatStatus = value => {
  if (!value) return '';
  const labels = {
    DECLINED: 'Reprovado',
    APPROVED: 'Aprovado',
    AVAILABLE_WITHOUT_REVIEW: 'Disponível sem revisão',
    VERIFIED: 'Verificado',
    GREEN: 'Alta',
    YELLOW: 'Média',
    RED: 'Baixa',
    LIVE: 'Ativo',
    CLOUD_API: 'Cloud API',
  };
  return labels[value] || String(value).replace(/_/g, ' ').toLowerCase();
};

onMounted(fetchProfile);
watch(() => props.inboxId, fetchProfile);
</script>

<template>
  <main class="flex-1 min-w-0 h-full overflow-auto bg-n-background">
    <section class="max-w-4xl px-6 py-8 mx-auto">
      <div class="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            Perfil do WhatsApp
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            Dados puxados diretamente da Meta para esta conexão.
          </p>
        </div>
        <div class="flex items-center gap-2">
          <Button
            v-if="!isEditing"
            label="Editar perfil"
            icon="i-lucide-pencil"
            size="sm"
            slate
            :disabled="isLoading || !profile"
            @click="isEditing = true"
          />
          <Button
            v-else
            label="Cancelar"
            icon="i-lucide-x"
            size="sm"
            slate
            :disabled="isSaving"
            @click="cancelEdit"
          />
          <Button
            label="Templates"
            icon="i-lucide-layout-template"
            size="sm"
            slate
            :is-loading="isLoadingTemplates"
            @click="openTemplateManager"
          />
          <Button
            label="Atualizar"
            icon="i-lucide-refresh-cw"
            size="sm"
            slate
            :is-loading="isLoading"
            :disabled="isSaving"
            @click="fetchProfile"
          />
        </div>
      </div>

      <div
        class="overflow-hidden border border-solid rounded-lg shadow-sm bg-n-alpha-3 border-n-weak"
      >
        <div class="p-6 text-center border-b border-solid border-n-weak">
          <div
            class="flex items-center justify-center w-28 h-28 mx-auto overflow-hidden rounded-full bg-n-blue-3 text-n-blue-11"
          >
            <img
              v-if="profileImage"
              :src="profileImage"
              :alt="displayName"
              class="object-cover w-full h-full"
              referrerpolicy="no-referrer"
              @error="imageFailed = true"
            />
            <span v-else class="text-3xl font-semibold">
              {{ initials }}
            </span>
          </div>
          <h2 class="mt-4 text-2xl font-semibold text-n-slate-12">
            {{ displayName }}
          </h2>
          <p class="mt-1 text-base text-n-slate-11">
            {{ displayPhone }}
          </p>
          <p
            v-if="phone?.verified_name"
            class="mt-1 text-sm text-n-slate-10"
          >
            Nome verificado na Meta
          </p>
          <Button
            v-if="profile?.wa_link"
            label="Compartilhar"
            icon="i-lucide-share-2"
            class="mt-4"
            slate
            @click="shareProfile"
          />
        </div>

        <div class="p-6 space-y-5">
          <div
            v-if="loadError"
            class="rounded-md border border-solid border-n-ruby-5 bg-n-ruby-2 px-3 py-2 text-sm text-n-ruby-11"
          >
            {{ loadError }}
          </div>

          <form
            v-if="isEditing"
            class="space-y-5"
            @submit.prevent="updateProfile"
          >
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label class="block">
                <span class="block mb-1 text-xs font-medium text-n-slate-10">
                  Status
                </span>
                <input
                  v-model="form.about"
                  class="w-full rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none"
                  maxlength="139"
                />
              </label>

              <label class="block">
                <span class="block mb-1 text-xs font-medium text-n-slate-10">
                  Categoria
                </span>
                <select
                  v-model="form.vertical"
                  class="w-full rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none"
                >
                  <option value="">Selecionar categoria</option>
                  <option
                    v-for="option in VERTICAL_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </label>

              <label class="block md:col-span-2">
                <span class="block mb-1 text-xs font-medium text-n-slate-10">
                  Descrição
                </span>
                <textarea
                  v-model="form.description"
                  rows="3"
                  class="w-full resize-y rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none"
                />
              </label>

              <label class="block md:col-span-2">
                <span class="block mb-1 text-xs font-medium text-n-slate-10">
                  Endereço
                </span>
                <input
                  v-model="form.address"
                  class="w-full rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none"
                />
              </label>

              <label class="block">
                <span class="block mb-1 text-xs font-medium text-n-slate-10">
                  E-mail
                </span>
                <input
                  v-model="form.email"
                  type="email"
                  class="w-full rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none"
                />
              </label>

              <label class="block">
                <span class="block mb-1 text-xs font-medium text-n-slate-10">
                  Foto do perfil por URL
                </span>
                <input
                  v-model="form.profile_picture_url"
                  type="url"
                  placeholder="https://..."
                  class="w-full rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none"
                />
              </label>

              <label class="block md:col-span-2">
                <span class="block mb-1 text-xs font-medium text-n-slate-10">
                  Sites
                </span>
                <textarea
                  v-model="form.websites"
                  rows="3"
                  placeholder="Um site por linha"
                  class="w-full resize-y rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none"
                />
              </label>
            </div>

            <div class="flex justify-end gap-2">
              <Button
                label="Cancelar"
                type="button"
                slate
                :disabled="isSaving"
                @click="cancelEdit"
              />
              <Button
                label="Salvar alterações"
                icon="i-lucide-save"
                type="submit"
                :is-loading="isSaving"
              />
            </div>
          </form>

          <div v-else-if="profileRows.length" class="space-y-4">
            <a
              v-for="row in profileRows"
              :key="`${row.icon}-${row.label}`"
              :href="row.href"
              :target="row.href ? '_blank' : undefined"
              rel="noopener noreferrer"
              class="flex items-start gap-3 text-sm text-n-slate-11"
            >
              <i :class="row.icon" class="mt-0.5 text-base text-n-slate-10" />
              <span class="min-w-0">
                <span class="block text-xs font-medium text-n-slate-10">
                  {{ row.title }}
                </span>
                <span class="block break-words text-n-slate-12">
                  {{ row.label }}
                </span>
              </span>
            </a>
          </div>

          <div
            v-if="statusItems.length"
            class="grid grid-cols-1 gap-3 pt-5 border-t border-solid sm:grid-cols-2 border-n-weak"
          >
            <div
              v-for="[label, value] in statusItems"
              :key="label"
              class="px-3 py-2 rounded-md bg-n-alpha-2"
            >
              <p class="text-xs text-n-slate-10">{{ label }}</p>
              <p class="mt-1 text-sm font-medium text-n-slate-12">
                {{ value }}
              </p>
            </div>
          </div>

          <p
            v-if="profile && !isEditing && !profileRows.length && !statusItems.length"
            class="m-0 text-sm text-n-slate-10"
          >
            A Meta retornou apenas os dados principais deste perfil.
          </p>
        </div>
      </div>

      <section
        v-if="showTemplateManager"
        class="mt-6 overflow-hidden border border-solid rounded-lg shadow-sm bg-n-alpha-3 border-n-weak"
      >
        <div
          class="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-solid border-n-weak"
        >
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              Templates do WhatsApp
            </h2>
            <p class="mt-1 text-sm text-n-slate-11">
              Gerencie modelos da Meta desta conexão.
            </p>
          </div>
          <div class="flex items-center gap-2">
            <Button
              label="Novo template"
              icon="i-lucide-plus"
              size="sm"
              @click="newTemplate"
            />
            <Button
              label="Atualizar lista"
              icon="i-lucide-refresh-cw"
              size="sm"
              slate
              :is-loading="isLoadingTemplates"
              @click="fetchTemplates"
            />
          </div>
        </div>

        <div class="grid grid-cols-1 gap-0 lg:grid-cols-[minmax(320px,420px)_1fr]">
          <div class="border-b border-solid lg:border-b-0 lg:border-r border-n-weak">
            <div class="grid grid-cols-1 gap-2 p-3 sm:grid-cols-3 lg:grid-cols-1">
              <input
                v-model="templateFilters.query"
                placeholder="Pesquisar template"
                class="w-full rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none"
              />
              <select
                v-model="templateFilters.status"
                class="w-full rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none"
              >
                <option value="">Todos os status</option>
                <option
                  v-for="status in TEMPLATE_STATUS_OPTIONS"
                  :key="status"
                  :value="status"
                >
                  {{ statusLabel(status) }}
                </option>
              </select>
              <select
                v-model="templateFilters.category"
                class="w-full rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none"
              >
                <option value="">Todas as categorias</option>
                <option
                  v-for="category in TEMPLATE_CATEGORY_OPTIONS"
                  :key="category"
                  :value="category"
                >
                  {{ categoryLabel(category) }}
                </option>
              </select>
            </div>

            <div class="max-h-[560px] overflow-auto px-3 pb-3">
              <button
                v-for="template in visibleTemplates"
                :key="`${template.id}-${template.name}-${template.language}`"
                type="button"
                class="mb-2 w-full rounded-md border border-solid px-3 py-2 text-left transition-colors"
                :class="
                  selectedTemplate?.id === template.id
                    ? 'border-n-blue-7 bg-n-blue-2'
                    : 'border-n-weak bg-n-alpha-2 hover:bg-n-alpha-3'
                "
                @click="selectTemplate(template)"
              >
                <span class="flex items-start justify-between gap-2">
                  <span class="min-w-0">
                    <span
                      class="block truncate text-sm font-medium text-n-slate-12"
                    >
                      {{ template.name }}
                    </span>
                    <span class="mt-1 line-clamp-2 text-xs text-n-slate-10">
                      {{ templateSummary(template) }}
                    </span>
                  </span>
                  <span
                    class="shrink-0 rounded-md px-2 py-0.5 text-xs font-medium"
                    :class="
                      template.status === 'APPROVED'
                        ? 'bg-n-teal-3 text-n-teal-11'
                        : 'bg-n-amber-3 text-n-amber-11'
                    "
                  >
                    {{ statusLabel(template.status) }}
                  </span>
                </span>
                <span class="mt-2 flex flex-wrap gap-1 text-xs text-n-slate-10">
                  <span class="rounded bg-n-alpha-3 px-1.5 py-0.5">
                    {{ categoryLabel(template.category) }}
                  </span>
                  <span class="rounded bg-n-alpha-3 px-1.5 py-0.5">
                    {{ template.language }}
                  </span>
                </span>
              </button>

              <p
                v-if="!isLoadingTemplates && !visibleTemplates.length"
                class="px-2 py-6 text-center text-sm text-n-slate-10"
              >
                Nenhum template encontrado.
              </p>
            </div>
          </div>

          <form class="p-4 space-y-4" @submit.prevent="saveTemplate">
            <div
              v-if="templateError"
              class="rounded-md border border-solid border-n-ruby-5 bg-n-ruby-2 px-3 py-2 text-sm text-n-ruby-11"
            >
              {{ templateError }}
            </div>

            <div class="flex items-start justify-between gap-3">
              <div>
                <h3 class="text-base font-semibold text-n-slate-12">
                  {{
                    templateMode === 'edit'
                      ? 'Editar template'
                      : 'Criar template'
                  }}
                </h3>
                <p class="mt-1 text-xs text-n-slate-10">
                  Alterações podem voltar para análise da Meta.
                </p>
              </div>
              <Button
                v-if="templateMode === 'edit'"
                label="Excluir"
                icon="i-lucide-trash-2"
                type="button"
                size="sm"
                ruby
                faded
                :disabled="isSavingTemplate"
                @click="deleteTemplate(selectedTemplate)"
              />
            </div>

            <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label class="block">
                <span class="block mb-1 text-xs font-medium text-n-slate-10">
                  Nome do modelo
                </span>
                <input
                  v-model="templateForm.name"
                  :disabled="templateMode === 'edit'"
                  placeholder="nome_do_template"
                  class="w-full rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none disabled:opacity-60"
                />
              </label>

              <label class="block">
                <span class="block mb-1 text-xs font-medium text-n-slate-10">
                  Idioma
                </span>
                <input
                  v-model="templateForm.language"
                  :disabled="templateMode === 'edit'"
                  placeholder="pt_BR"
                  class="w-full rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none disabled:opacity-60"
                />
              </label>

              <label class="block">
                <span class="block mb-1 text-xs font-medium text-n-slate-10">
                  Categoria
                </span>
                <select
                  v-model="templateForm.category"
                  class="w-full rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none"
                >
                  <option
                    v-for="category in TEMPLATE_CATEGORY_OPTIONS"
                    :key="category"
                    :value="category"
                  >
                    {{ categoryLabel(category) }}
                  </option>
                </select>
              </label>

              <label class="block">
                <span class="block mb-1 text-xs font-medium text-n-slate-10">
                  Formato de parâmetro
                </span>
                <select
                  v-model="templateForm.parameter_format"
                  class="w-full rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none"
                >
                  <option value="POSITIONAL">Posicional</option>
                  <option value="NAMED">Nomeado</option>
                </select>
              </label>
            </div>

            <label class="block">
              <span class="block mb-1 text-xs font-medium text-n-slate-10">
                Componentes Meta JSON
              </span>
              <textarea
                v-model="templateForm.componentsText"
                rows="12"
                spellcheck="false"
                class="w-full resize-y rounded-md border border-solid border-n-weak bg-n-alpha-2 px-3 py-2 font-mono text-xs text-n-slate-12 outline-none"
              />
            </label>

            <div
              v-if="selectedTemplate"
              class="rounded-md bg-n-alpha-2 p-3 text-sm text-n-slate-11"
            >
              <p class="mb-2 text-xs font-medium uppercase text-n-slate-10">
                Resumo
              </p>
              <p class="whitespace-pre-wrap text-n-slate-12">
                {{ templateSummary(selectedTemplate) }}
              </p>
              <div class="mt-3 flex flex-wrap gap-2 text-xs">
                <span class="rounded bg-n-alpha-3 px-2 py-1">
                  {{ statusLabel(selectedTemplate.status) }}
                </span>
                <span class="rounded bg-n-alpha-3 px-2 py-1">
                  {{ categoryLabel(selectedTemplate.category) }}
                </span>
                <span class="rounded bg-n-alpha-3 px-2 py-1">
                  {{ selectedTemplate.language }}
                </span>
              </div>
            </div>

            <div class="flex justify-end gap-2">
              <Button
                label="Limpar"
                type="button"
                slate
                :disabled="isSavingTemplate"
                @click="newTemplate"
              />
              <Button
                :label="
                  templateMode === 'edit'
                    ? 'Salvar edição'
                    : 'Criar modelo'
                "
                icon="i-lucide-save"
                type="submit"
                :is-loading="isSavingTemplate"
              />
            </div>
          </form>
        </div>
      </section>
    </section>
  </main>
</template>
